import { Link } from "react-router-dom";
import { navigateToTop } from "../utils/Constants";
import "../styles/pages/_error.scss";

// ─── Quick links ──────────────────────────────────────────────────────────────
const QUICK_LINKS = [
    { to: "/dashboard", label: "Dashboard" },
    { to: "/dashboard/questions", label: "Questions" },
    { to: "/help", label: "Help centre" },
    { to: "/", label: "Home" },
];

// ─── Icons ────────────────────────────────────────────────────────────────────
const IconChevLeft = () => (
    <svg
        width="15"
        height="15"
        viewBox="0 0 15 15"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M10 2.5L5 7.5l5 5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const IconArrow = () => (
    <svg
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M2.5 6h7M6.5 3L9.5 6l-3 3"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

// Camera-with-cross illustration — brand tones, no white
function CameraIllustration() {
    return (
        <svg
            className="err-illustration"
            viewBox="0 0 120 96"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
        >
            {/* Camera body */}
            <rect
                x="16"
                y="28"
                width="88"
                height="58"
                rx="7"
                fill="rgba(50,36,123,0.06)"
                stroke="rgba(50,36,123,0.22)"
                strokeWidth="1.5"
            />
            {/* Viewfinder notch */}
            <path
                d="M44 28v-8a4 4 0 0 1 4-4h24a4 4 0 0 1 4 4v8"
                stroke="rgba(50,36,123,0.22)"
                strokeWidth="1.5"
                strokeLinejoin="round"
            />
            {/* Lens ring */}
            <circle
                cx="60"
                cy="57"
                r="18"
                fill="rgba(50,36,123,0.04)"
                stroke="rgba(50,36,123,0.18)"
                strokeWidth="1.5"
            />
            <circle
                cx="60"
                cy="57"
                r="11"
                fill="rgba(50,36,123,0.04)"
                stroke="rgba(50,36,123,0.14)"
                strokeWidth="1.2"
            />
            {/* Cross — "not found" */}
            <line
                x1="51"
                y1="48"
                x2="69"
                y2="66"
                stroke="rgba(185,28,28,0.55)"
                strokeWidth="1.8"
                strokeLinecap="round"
            />
            <line
                x1="69"
                y1="48"
                x2="51"
                y2="66"
                stroke="rgba(185,28,28,0.55)"
                strokeWidth="1.8"
                strokeLinecap="round"
            />
            {/* Record indicator dot */}
            <circle
                cx="88"
                cy="36"
                r="5"
                fill="rgba(185,28,28,0.18)"
                stroke="rgba(185,28,28,0.4)"
                strokeWidth="1.2"
            />
            <circle cx="88" cy="36" r="2.5" fill="rgba(185,28,28,0.5)" />
            {/* Signal off arcs */}
            <path
                d="M38 16 Q60 2 82 16"
                stroke="rgba(50,36,123,0.1)"
                strokeWidth="1.8"
                strokeLinecap="round"
                fill="none"
            />
            <path
                d="M46 22 Q60 12 74 22"
                stroke="rgba(50,36,123,0.16)"
                strokeWidth="1.8"
                strokeLinecap="round"
                fill="none"
            />
            {/* Strike through */}
            <line
                x1="26"
                y1="6"
                x2="94"
                y2="30"
                stroke="rgba(185,28,28,0.3)"
                strokeWidth="1.2"
                strokeLinecap="round"
                strokeDasharray="4 3"
            />
        </svg>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function Error() {
    const hitTimestamp = new Date().toLocaleString();

    return (
        <main className="err-page" role="main" aria-labelledby="err-title">
            {/* Ambient background */}
            <div className="err-bg" aria-hidden="true">
                <div className="err-bg__orb err-bg__orb--1" />
                <div className="err-bg__orb err-bg__orb--2" />
            </div>

            <div className="err-shell">
                {/* Back link */}
                {/* <Link to="/" onClick={navigateToTop} className="err-back">
                    <IconChevLeft /> Back to home
                </Link> */}

                {/* Main card */}
                <div className="err-card">
                    {/* Illustration */}
                    <div className="err-card__visual" aria-hidden="true">
                        <CameraIllustration />
                    </div>

                    {/* Heading group */}
                    <div className="err-card__heading">
                        <span className="err-card__eyebrow">404</span>
                        <h1 className="err-card__title" id="err-title">
                            Page not found
                        </h1>
                        <p className="err-card__body">
                            The page you're looking for doesn't exist or has
                            been moved. Check the URL for typos, or use one of
                            the links below to get back on track.
                        </p>
                    </div>

                    {/* Quick nav */}
                    <nav className="err-nav" aria-label="Recovery navigation">
                        {QUICK_LINKS.map(({ to, label }, i) => (
                            <Link
                                key={to}
                                to={to}
                                onClick={navigateToTop}
                                className={`err-nav__link${i === 0 ? " err-nav__link--primary" : ""}`}
                            >
                                {label} <IconArrow />
                            </Link>
                        ))}
                    </nav>

                    {/* Divider */}
                    <div className="err-card__divider" aria-hidden="true" />

                    {/* Log — honest, understated */}
                    <div className="err-log" aria-label="Error log">
                        <p className="err-log__heading">Error log</p>
                        <div className="err-log__rows">
                            <div className="err-log__row">
                                <span className="err-log__ts">00:00</span>
                                <span className="err-log__text">
                                    Request received. Route matching initiated.
                                </span>
                            </div>
                            <div className="err-log__row">
                                <span className="err-log__ts">00:01</span>
                                <span className="err-log__text">
                                    No matching route found. Falling back to 404
                                    handler.
                                </span>
                            </div>
                            <div className="err-log__row">
                                <span className="err-log__ts">00:02</span>
                                <span className="err-log__text">
                                    Error boundary triggered. Rendering this
                                    page.
                                </span>
                            </div>
                            <div className="err-log__row err-log__row--meta">
                                <span className="err-log__ts">now</span>
                                <span className="err-log__text">
                                    {hitTimestamp}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
}
